import AnnouncementBlock from '../components/site/blocks/AnnouncementBlock.vue'
import HeadingBlock from '../components/site/blocks/HeadingBlock.vue'
import RichTextBlock from '../components/site/blocks/RichTextBlock.vue'
import ImageBlock from '../components/site/blocks/ImageBlock.vue'
import ContentSectionBlock from '../components/site/blocks/ContentSectionBlock.vue'
import CtaBannerBlock from '../components/site/blocks/CtaBannerBlock.vue'
import TestimonialBlock from '../components/site/blocks/TestimonialBlock.vue'
import StatsBlock from '../components/site/blocks/StatsBlock.vue'
import CardsGridBlock from '../components/site/blocks/CardsGridBlock.vue'
import TwoColumnBlock from '../components/site/blocks/TwoColumnBlock.vue'
import FaqBlock from '../components/site/blocks/FaqBlock.vue'
import LogoStripBlock from '../components/site/blocks/LogoStripBlock.vue'
import HeroBlock from '../components/site/blocks/HeroBlock.vue'
import PricingBlock from '../components/site/blocks/PricingBlock.vue'
import StepsBlock from '../components/site/blocks/StepsBlock.vue'
import GalleryBlock from '../components/site/blocks/GalleryBlock.vue'
import TabsAccordionBlock from '../components/site/blocks/TabsAccordionBlock.vue'
import NewsletterBlock from '../components/site/blocks/NewsletterBlock.vue'
import ContactFormBlock from '../components/site/blocks/ContactFormBlock.vue'
import DynamicFormBlock from '../components/site/blocks/DynamicFormBlock.vue'
import EmbedBlock from '../components/site/blocks/EmbedBlock.vue'
import CountdownBlock from '../components/site/blocks/CountdownBlock.vue'
import TeamBlock from '../components/site/blocks/TeamBlock.vue'
import CustomHtmlBlock from '../components/site/blocks/CustomHtmlBlock.vue'
import ColumnsBlock from '../components/site/blocks/ColumnsBlock.vue'

import AnnouncementSettings from '../components/site/settings/AnnouncementSettings.vue'
import HeadingSettings from '../components/site/settings/HeadingSettings.vue'
import RichTextSettings from '../components/site/settings/RichTextSettings.vue'
import ImageSettings from '../components/site/settings/ImageSettings.vue'
import ContentSectionSettings from '../components/site/settings/ContentSectionSettings.vue'
import CtaBannerSettings from '../components/site/settings/CtaBannerSettings.vue'
import TestimonialSettings from '../components/site/settings/TestimonialSettings.vue'
import StatsSettings from '../components/site/settings/StatsSettings.vue'
import CardsGridSettings from '../components/site/settings/CardsGridSettings.vue'
import TwoColumnSettings from '../components/site/settings/TwoColumnSettings.vue'
import FaqSettings from '../components/site/settings/FaqSettings.vue'
import LogoStripSettings from '../components/site/settings/LogoStripSettings.vue'
import HeroSettings from '../components/site/settings/HeroSettings.vue'
import PricingSettings from '../components/site/settings/PricingSettings.vue'
import StepsSettings from '../components/site/settings/StepsSettings.vue'
import GallerySettings from '../components/site/settings/GallerySettings.vue'
import TabsAccordionSettings from '../components/site/settings/TabsAccordionSettings.vue'
import NewsletterSettings from '../components/site/settings/NewsletterSettings.vue'
import ContactFormSettings from '../components/site/settings/ContactFormSettings.vue'
import DynamicFormSettings from '../components/site/settings/DynamicFormSettings.vue'
import EmbedSettings from '../components/site/settings/EmbedSettings.vue'
import CountdownSettings from '../components/site/settings/CountdownSettings.vue'
import TeamSettings from '../components/site/settings/TeamSettings.vue'
import CustomHtmlSettings from '../components/site/settings/CustomHtmlSettings.vue'
import ColumnsSettings from '../components/site/settings/ColumnsSettings.vue'

/**
 * Registers the layer's built-in generic blocks at app init — renderer,
 * editor form, palette entry and default payload for each. Runs on both
 * server and client so SSR page renders can resolve these components.
 *
 * Consuming apps add their own domain-specific blocks in their own plugin
 * (e.g. agency-frontend/plugins/site-blocks.ts). Both run before first render,
 * so the registry holds every type in use.
 *
 * `meta.order` sets the curated palette sequence and is deliberately
 * independent of registration order — apps interleave their domain blocks by
 * choosing weights between these.
 */
export default defineNuxtPlugin(() => {
  registerSiteBlock('announcement', {
    component: AnnouncementBlock,
    settings: AnnouncementSettings,
    meta: { label: 'Announcement', icon: 'i-lucide-megaphone', order: 5, category: 'layout', description: 'Slim full-width notice with a link.' },
    // The only block that opts out of the renderer's centered wrapper.
    bleed: true,
    defaultData: {
      text: '',
      link_text: '',
      link_url: '',
      tone: 'accent',
      dismissible: false
    }
  })

  registerSiteBlock('hero', {
    component: HeroBlock,
    settings: HeroSettings,
    meta: { label: 'Hero', icon: 'i-lucide-panels-top-left', order: 10, category: 'content', description: 'Big headline, subtext and button at the top of a page.' },
    defaultData: {
      eyebrow: '',
      headline: 'Your headline here',
      subtext: '',
      button_text: '',
      button_url: '',
      image: '',
      align: 'center',
      overlay: true
    }
  })

  registerSiteBlock('heading', {
    component: HeadingBlock,
    settings: HeadingSettings,
    meta: { label: 'Heading', icon: 'i-lucide-heading', order: 20, category: 'content', description: 'A section title.' },
    defaultData: { text: 'New heading', level: 2 }
  })

  registerSiteBlock('rich_text', {
    component: RichTextBlock,
    settings: RichTextSettings,
    meta: { label: 'Rich text', icon: 'i-lucide-pilcrow', order: 30, category: 'content', description: 'Paragraphs, lists and links.' },
    defaultData: { html: '<p>Write something…</p>' }
  })

  registerSiteBlock('image', {
    component: ImageBlock,
    settings: ImageSettings,
    meta: { label: 'Image', icon: 'i-lucide-image', order: 40, category: 'media', description: 'A single image with optional caption.' },
    defaultData: { url: '', alt: '' }
  })

  registerSiteBlock('content_section', {
    component: ContentSectionBlock,
    settings: ContentSectionSettings,
    meta: { label: 'Content section', icon: 'i-lucide-image-play', order: 65, category: 'content', description: 'Title and text next to an image or YouTube video.' },
    defaultData: { headline: '', subtext: '', media_type: null, media_url: '', media_link: '', layout: 'right' }
  })

  registerSiteBlock('cta_banner', {
    component: CtaBannerBlock,
    settings: CtaBannerSettings,
    meta: { label: 'CTA banner', icon: 'i-lucide-sparkles', order: 70, category: 'conversion', description: 'A highlighted banner with one button.' },
    defaultData: { headline: 'Ready to get started?', button_text: 'Contact us', button_url: '' }
  })

  registerSiteBlock('testimonial', {
    component: TestimonialBlock,
    settings: TestimonialSettings,
    meta: { label: 'Testimonial', icon: 'i-lucide-quote', order: 80, category: 'social_proof', description: 'One quote with author.' },
    defaultData: { quote: '', author: '' }
  })

  registerSiteBlock('stats', {
    component: StatsBlock,
    settings: StatsSettings,
    meta: { label: 'Stats', icon: 'i-lucide-bar-chart-3', order: 90, category: 'social_proof', description: 'Row of key figures, e.g. 100+ members.' },
    defaultData: { headline: '', stats: [{ value: '100+', label: 'Members' }] }
  })

  registerSiteBlock('cards_grid', {
    component: CardsGridBlock,
    settings: CardsGridSettings,
    meta: { label: 'Cards grid', icon: 'i-lucide-layout-grid', order: 100, category: 'content', description: 'Grid of cards with title, text and optional image.' },
    defaultData: {
      headline: '',
      columns: 3,
      cards: [
        { title: 'Card one', description: 'A short line about this card.' },
        { title: 'Card two', description: 'A short line about this card.' },
        { title: 'Card three', description: 'A short line about this card.' }
      ]
    }
  })

  registerSiteBlock('two_column', {
    component: TwoColumnBlock,
    settings: TwoColumnSettings,
    meta: { label: 'Two column', icon: 'i-lucide-columns-2', order: 110, category: 'layout', description: 'Two side-by-side text blocks.' },
    defaultData: { left_title: '', left_html: '<p></p>', right_title: '', right_html: '<p></p>' }
  })

  registerSiteBlock('faq', {
    component: FaqBlock,
    settings: FaqSettings,
    meta: { label: 'FAQ', icon: 'i-lucide-circle-help', order: 120, category: 'content', description: 'Expandable questions and answers.' },
    defaultData: {
      headline: 'Frequently asked questions',
      faqs: [
        { question: 'Your first question here?', answer: 'Your answer here.' },
        { question: 'Your second question here?', answer: 'Your answer here.' }
      ]
    }
  })

  registerSiteBlock('logo_strip', {
    component: LogoStripBlock,
    settings: LogoStripSettings,
    meta: { label: 'Logo strip', icon: 'i-lucide-gallery-horizontal-end', order: 130, category: 'social_proof', description: 'Row of partner or client logos.' },
    defaultData: { headline: '', logos: [] }
  })

  registerSiteBlock('steps', {
    component: StepsBlock,
    settings: StepsSettings,
    meta: { label: 'How it works', icon: 'i-lucide-list-ordered', order: 75, category: 'content', description: 'Numbered process or timeline.' },
    defaultData: {
      headline: '',
      subtext: '',
      orientation: 'horizontal',
      steps: [
        { title: 'Step one', description: 'What happens first.' },
        { title: 'Step two', description: 'What happens next.' },
        { title: 'Step three', description: 'What happens last.' }
      ]
    }
  })

  registerSiteBlock('team', {
    component: TeamBlock,
    settings: TeamSettings,
    meta: { label: 'Team', icon: 'i-lucide-users', order: 85, category: 'social_proof', description: 'People with photo, role and socials.' },
    // `source: 'manual'` is seeded so an app that adds a live-data mode can
    // switch on it without having to treat undefined as a third case.
    defaultData: {
      headline: '',
      source: 'manual',
      members: [
        { name: 'Jane Doe', role: 'Founder' },
        { name: 'John Smith', role: 'Instructor' }
      ]
    }
  })

  registerSiteBlock('pricing', {
    component: PricingBlock,
    settings: PricingSettings,
    meta: { label: 'Pricing', icon: 'i-lucide-circle-dollar-sign', order: 95, category: 'conversion', description: 'Manual plans with features and buttons.' },
    defaultData: {
      headline: '',
      subtext: '',
      plans: [
        { name: 'Free', price: '$0', features: ['Feature one', 'Feature two'] },
        { name: 'Pro', price: '$29', features: ['Everything in Free', 'Feature three'], highlighted: true }
      ]
    }
  })

  registerSiteBlock('gallery', {
    component: GalleryBlock,
    settings: GallerySettings,
    meta: { label: 'Gallery', icon: 'i-lucide-images', order: 105, category: 'media', description: 'Image grid or carousel.' },
    defaultData: { headline: '', mode: 'grid', columns: 3, images: [] }
  })

  registerSiteBlock('tabs_accordion', {
    component: TabsAccordionBlock,
    settings: TabsAccordionSettings,
    meta: { label: 'Tabs / accordion', icon: 'i-lucide-list-collapse', order: 115, category: 'content', description: 'Content split into tabs or panels.' },
    defaultData: {
      headline: '',
      display: 'accordion',
      panels: [
        { title: 'First panel', html: '<p>Content for the first panel.</p>' },
        { title: 'Second panel', html: '<p>Content for the second panel.</p>' }
      ]
    }
  })

  registerSiteBlock('countdown', {
    component: CountdownBlock,
    settings: CountdownSettings,
    meta: { label: 'Countdown', icon: 'i-lucide-timer', order: 125, category: 'conversion', description: 'Timer to a launch or event date.' },
    defaultData: {
      headline: '',
      subtext: '',
      target_at: '',
      expired_text: '',
      button_text: '',
      button_url: '',
      labels: {}
    }
  })

  registerSiteBlock('newsletter', {
    component: NewsletterBlock,
    settings: NewsletterSettings,
    meta: { label: 'Newsletter signup', icon: 'i-lucide-mail', order: 135, category: 'conversion', description: 'Email signup posting to your provider.' },
    defaultData: {
      headline: '',
      subtext: '',
      action_url: '',
      method: 'post',
      target: 'blank',
      email_field_name: 'email',
      placeholder: '',
      button_text: '',
      success_text: '',
      consent_text: '',
      hidden_fields: []
    }
  })

  registerSiteBlock('contact_form', {
    component: ContactFormBlock,
    settings: ContactFormSettings,
    meta: { label: 'Contact form', icon: 'i-lucide-send', order: 145, category: 'conversion', description: 'Form posting to an external endpoint.' },
    // Seeded with the classic three fields, so the out-of-the-box block is the
    // fixed form most tenants want; the list is editable for the rest.
    defaultData: {
      headline: '',
      subtext: '',
      action_url: '',
      method: 'post',
      target: 'blank',
      button_text: '',
      success_text: '',
      consent_text: '',
      fields: [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'email', required: true },
        { name: 'message', label: 'Message', type: 'textarea', required: true }
      ],
      hidden_fields: []
    }
  })

  registerSiteBlock('dynamic_form', {
    component: DynamicFormBlock,
    settings: DynamicFormSettings,
    meta: { label: 'Structured form (with file upload)', icon: 'i-lucide-file-up', order: 150, category: 'conversion', description: 'Custom fields, submitted to this platform.' },
    // Unlike contact_form, submit_url is meant to point at this platform's
    // own public API (e.g. /api/public/forms/{slug}/submit), not a third
    // party — that's what lets this block accept files and show real
    // success/error responses instead of a fire-and-forget POST.
    defaultData: {
      headline: '',
      subtext: '',
      submit_url: '',
      button_text: '',
      submitting_text: '',
      error_text: '',
      success_text: '',
      consent_text: '',
      fields: [
        { name: 'full_name', label: 'Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'email', required: true },
        { name: 'message', label: 'Message', type: 'textarea', required: false }
      ]
    }
  })

  registerSiteBlock('embed', {
    component: EmbedBlock,
    settings: EmbedSettings,
    meta: { label: 'Embed', icon: 'i-lucide-frame', order: 165, category: 'media', description: 'YouTube, Vimeo, Calendly, Maps, Typeform…' },
    defaultData: { headline: '', provider: 'calendly', url: '', height: 600, caption: '' }
  })

  registerSiteBlock('custom_html', {
    component: CustomHtmlBlock,
    settings: CustomHtmlSettings,
    meta: { label: 'Custom HTML', icon: 'i-lucide-code-2', order: 170, category: 'advanced', description: 'Paste raw HTML (runs in a sandbox).' },
    defaultData: { url: '', html: '' }
  })

  registerSiteBlock('columns', {
    component: ColumnsBlock,
    settings: ColumnsSettings,
    meta: {
      label: 'Columns',
      icon: 'i-lucide-columns-3',
      order: 8,
      category: 'layout',
      description: 'Place blocks side by side in 2–4 columns.'
    },
    container: true,
    defaultData: { ratio: '1-1', gap: 'm', vertical_align: 'top', stack_on_mobile: true, cols: [] },
    // Column ids are drop-zone keys and error-path anchors, so each instance
    // needs its own rather than a shared default.
    init: data => ({
      ...data,
      cols: [{ id: makeSiteBlockId(), blocks: [] }, { id: makeSiteBlockId(), blocks: [] }]
    })
  })
})
